import React, { ReactNode, useEffect, useMemo } from 'react';
import { PaginationButtons } from 'apps/sigma2-ui/src/components/ui/PaginationButtons';
import { usePagination } from 'apps/sigma2-ui/src/lib/hooks/usePagination';

export interface UseTypedPaginationParams<T> {
  items: T[];
  onPageChange?: (pageIndex: number) => void;
}

export interface UseTypedPaginationPayload<T> {
  selectedItem: T | null;
  carouselSwitcher: ReactNode;
}

export const useTypedPagination = <T,>({
  items,
  onPageChange,
}: UseTypedPaginationParams<T>): UseTypedPaginationPayload<T> => {
  const {
    currentPage,
    currentPageIndex,
    onPreviousPageClick,
    onNextPageClick,
  } = usePagination({
    totalPages: items.length,
  });

  const selectedItem = useMemo(() => {
    if (items.length === 0) {
      return null;
    }

    return items[currentPageIndex];
  }, [items, currentPageIndex]);

  useEffect(() => {
    if (onPageChange) {
      onPageChange(currentPageIndex);
    }
  }, [onPageChange, currentPageIndex]);

  const carouselSwitcherNode: ReactNode = useMemo(() => {
    return (
      <div>
        <PaginationButtons
          totalPages={items.length}
          currentPage={currentPage}
          onPreviousPageClick={onPreviousPageClick}
          onNextPageClick={onNextPageClick}
        />
      </div>
    );
  }, [items, currentPage, onPreviousPageClick, onNextPageClick]);

  return useMemo(() => {
    return {
      selectedItem,
      carouselSwitcher: carouselSwitcherNode,
    };
  }, [carouselSwitcherNode, selectedItem]);
};
